import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import ru from "./ru.json";
import en from "./en.json";
import { setRouteMeta } from "./seo";

// Interface languages: add a locale file + one entry here. "ru" is the default (no URL prefix).
export const UI_LANGUAGES = [
  { code: "ru", label: "RU", dict: ru },
  { code: "en", label: "EN", dict: en },
];
const DEFAULT = "ru";
const STORAGE_KEY = "uiLanguage";
const dicts = Object.fromEntries(UI_LANGUAGES.map((l) => [l.code, l.dict]));

const prefixRe = new RegExp(`^/(${UI_LANGUAGES.filter((l) => l.code !== DEFAULT).map((l) => l.code).join("|")})(?=/|$)`);

export const langFromPath = (pathname) => pathname.match(prefixRe)?.[1] || DEFAULT;
export const stripLang = (pathname) => pathname.replace(prefixRe, "") || "/";
export const withLang = (path, lang) => (lang === DEFAULT ? path : `/${lang}${path === "/" ? "" : path}`);

const LanguageContext = createContext(null);

// Current UI language, set synchronously by LanguageProvider on every render so plain functions can translate.
let currentLang = DEFAULT;
export const getCurrentLang = () => currentLang;
export const uiLocale = () => (currentLang === "en" ? "en-GB" : "ru-RU");
// Translate by Russian source text (gettext-style); untranslated text falls back to Russian.
export const tr = (text) => (currentLang === DEFAULT ? text : dicts[currentLang]?.[text] ?? text);

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, updateProfile } = useAuth();
  const lang = langFromPath(location.pathname);
  currentLang = lang;
  // Choice made in this session wins over a not-yet-refreshed profile value.
  const [chosen, setChosen] = useState(null);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // hreflang alternates for search engines: every page ↔ its counterpart in each UI language, x-default = Russian.
  useEffect(() => {
    setRouteMeta(location.pathname);
    const base = stripLang(location.pathname);
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    const entries = [...UI_LANGUAGES.map((l) => [l.code, withLang(base, l.code)]), ["x-default", withLang(base, DEFAULT)]];
    entries.forEach(([hreflang, path]) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = window.location.origin + path;
      document.head.appendChild(el);
    });
  }, [location.pathname]);

  // Unprefixed URL + saved non-default choice → go to that language. Prefixed URLs always win.
  useEffect(() => {
    if (loading || lang !== DEFAULT || location.pathname.startsWith("/auth/")) return;
    const saved = chosen || user?.uiLanguage || localStorage.getItem(STORAGE_KEY);
    if (saved && saved !== DEFAULT && dicts[saved]) {
      navigate(withLang(location.pathname, saved) + location.search + location.hash, { replace: true });
    }
  }, [loading, user, chosen, lang, location.pathname, location.search, location.hash, navigate]);

  const setLanguage = (code) => {
    if (code === lang) return;
    setChosen(code);
    localStorage.setItem(STORAGE_KEY, code);
    if (user) updateProfile({ uiLanguage: code });
    navigate(withLang(stripLang(location.pathname), code) + location.search + location.hash);
  };

  const t = (key) => dicts[lang]?.[key] ?? dicts[DEFAULT][key] ?? key;
  const localePath = (path) => withLang(path, lang);

  return (
    <LanguageContext.Provider value={{ lang, t, localePath, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
